import { FC } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { EmissionsByMonthItem } from "pages/cpiReports/types";

interface Co2ByMonthChartProps {
  data: EmissionsByMonthItem[] | null | undefined;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}

const CURRENT_MONTH = format(new Date(), "yyyy-MM");

/**
 * Mini single-series bar chart of monthly CO2 (WtW), tonnes. The current
 * (partial) month is hatched; the latest bar is directly labelled. Recessive
 * baseline, brand chart-1 fill, token-driven so it works in light and dark.
 */
const Co2ByMonthChart: FC<Co2ByMonthChartProps> = ({
  data,
  loading = false,
  error = false,
  onRetry,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="co2chart co2chart--loading" aria-hidden="true">
        <div className="co2chart__bars">
          {Array.from({ length: 8 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="co2chart__bar" key={i}>
              <span
                className="co2chart__fill co2chart__fill--skeleton"
                style={{ height: `${30 + ((i * 37) % 60)}%` }}
              />
            </div>
          ))}
        </div>
        <div className="co2chart__baseline" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="co2chart__msg">
        <span>{t("dashboard.tileError")}</span>
        {onRetry && (
          <button type="button" onClick={onRetry} className="kpi-tile__retry">
            {t("retry")}
          </button>
        )}
      </div>
    );
  }

  const months = (data ?? []).map((m) => ({
    month: m.month,
    value: m.currentEmissionsKg / 1000, // kg -> tonnes
  }));

  if (months.length === 0) {
    return <div className="co2chart__msg">{t("dashboard.noEmissions")}</div>;
  }

  const max = Math.max(...months.map((m) => m.value), 1);

  return (
    <div className="co2chart">
      <div className="co2chart__bars">
        {months.map((m, i) => {
          const partial = m.month === CURRENT_MONTH;
          const isLast = i === months.length - 1;
          const height = Math.max(4, (m.value / max) * 100);
          return (
            <div className="co2chart__bar" key={m.month}>
              {isLast && (
                <b className="co2chart__val">
                  {Math.round(m.value).toLocaleString()}
                </b>
              )}
              <span
                className={`co2chart__fill${partial ? " is-partial" : ""}`}
                style={{ height: `${height}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="co2chart__baseline" />
      <div className="co2chart__months">
        {months.map((m) => (
          <span key={m.month}>
            {format(parseISO(`${m.month}-01`), "MMM")}
            {m.month === CURRENT_MONTH ? "*" : ""}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Co2ByMonthChart;
